import { useState } from "react";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { CloudConnectionEndpoints } from "@generated/cloud-connections";
import { apiBaseUrls } from "src/platform/constants/shared-constants";

interface GcpSetupInfo {
  rushaServiceAccount: string;
  workloadIdentityPool: string;
  providerName: string;
  oidcIssuerUri: string;
}

interface ValidationResult {
  success: boolean;
  message: string;
}

interface Props {
  organizationAlias: string;
}

export function GcpSetupPanel({ organizationAlias }: Props) {
  const [setupInfo, setSetupInfo] = useState<GcpSetupInfo | null>(null);
  const [loadingInfo, setLoadingInfo] = useState(false);
  const [gcpProjectId, setGcpProjectId] = useState("");
  const [gcpWifProvider, setGcpWifProvider] = useState("");
  const [connectionName, setConnectionName] = useState("GCP");
  const [validating, setValidating] = useState(false);
  const [result, setResult] = useState<ValidationResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchSetupInfo = async () => {
    setLoadingInfo(true);
    setError(null);
    try {
      const url = `${apiBaseUrls.BACKEND}${CloudConnectionEndpoints.GCP_SETUP_INFO}?organizationAlias=${encodeURIComponent(organizationAlias)}`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) throw new Error(await res.text());
      setSetupInfo(await res.json());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load setup info");
    } finally {
      setLoadingInfo(false);
    }
  };

  const validate = async () => {
    if (!gcpProjectId || !gcpWifProvider) return;
    setValidating(true);
    setResult(null);
    setError(null);
    try {
      const res = await fetch(
        `${apiBaseUrls.BACKEND}${CloudConnectionEndpoints.GCP_VALIDATE}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            organizationAlias,
            name: connectionName || "GCP",
            gcpProjectId,
            gcpWifProvider,
          }),
        },
      );
      if (!res.ok) throw new Error(await res.text());
      setResult(await res.json());
    } catch (e) {
      setError(e instanceof Error ? e.message : "Validation failed");
    } finally {
      setValidating(false);
    }
  };

  const copyToClipboard = (text: string) => navigator.clipboard.writeText(text);

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        GCP Workload Identity Federation
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Grant Rusha&apos;s service account access to your GCP project via
        Workload Identity Federation — no long-lived service account keys
        required.
      </Typography>

      {!setupInfo && (
        <Button
          variant="outlined"
          onClick={fetchSetupInfo}
          disabled={loadingInfo}
          startIcon={loadingInfo ? <CircularProgress size={16} /> : undefined}
        >
          Show setup instructions
        </Button>
      )}

      {setupInfo && (
        <>
          {[
            {
              label: "Rusha Service Account",
              value: setupInfo.rushaServiceAccount,
            },
            {
              label: "Workload Identity Pool",
              value: setupInfo.workloadIdentityPool,
            },
            { label: "Provider Name", value: setupInfo.providerName },
          ].map(({ label, value }) => (
            <Box key={label} sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {label}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  variant="body2"
                  fontFamily="monospace"
                  sx={{ wordBreak: "break-all" }}
                >
                  {value}
                </Typography>
                <Tooltip title="Copy">
                  <IconButton
                    size="small"
                    onClick={() => copyToClipboard(value)}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
          ))}

          <Alert severity="info" sx={{ mb: 2 }}>
            Run the following to grant access:
            <Box
              component="pre"
              sx={{ mt: 1, fontSize: "0.75rem", overflow: "auto" }}
            >
              {`gcloud projects add-iam-policy-binding YOUR_PROJECT_ID \\
  --member="serviceAccount:${setupInfo.rushaServiceAccount}" \\
  --role="roles/viewer"`}
            </Box>
          </Alert>

          <Divider sx={{ mb: 2 }} />

          <Typography variant="body2" sx={{ mb: 1 }}>
            Enter your GCP project details to verify the connection:
          </Typography>
          <Box
            sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 1.5 }}
          >
            <TextField
              label="Connection name"
              placeholder="Staging GCP"
              value={connectionName}
              onChange={(e) => setConnectionName(e.target.value)}
              size="small"
            />
            <TextField
              label="GCP Project ID"
              placeholder="my-gcp-project"
              value={gcpProjectId}
              onChange={(e) => setGcpProjectId(e.target.value)}
              size="small"
            />
            <TextField
              label="WIF Provider Resource Name"
              placeholder="projects/my-project/locations/global/workloadIdentityPools/..."
              value={gcpWifProvider}
              onChange={(e) => setGcpWifProvider(e.target.value)}
              size="small"
            />
          </Box>
          <Button
            variant="contained"
            onClick={validate}
            disabled={validating || !gcpProjectId || !gcpWifProvider}
            startIcon={
              validating ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            Verify connection
          </Button>
        </>
      )}

      {result && (
        <Alert
          severity={result.success ? "success" : "error"}
          icon={result.success ? <CheckCircleIcon /> : undefined}
          sx={{ mt: 2 }}
        >
          {result.message}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
}
